import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { StampSerial, RM } from "@/components/shared";
import { CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";

export default function Reconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/recon/preview").then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  return (
    <Layout
      title="Rekonsiliasi / Reconciliation"
      subtitle="Re-sum share transactions vs cached running balances — must tie to zero variance"
      actions={<button onClick={load} data-testid="recon-refresh" className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><RefreshCw size={15} /> Refresh</button>}
    >
      {loading || !data ? (
        <div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Reconciling…</div>
      ) : (
        <>
          <div className={`rounded-sm border p-6 mb-6 flex items-center gap-4 ${data.in_balance ? "bg-positive-bg border-positive/30" : "bg-brick-bg border-brick/30"}`} data-testid="recon-banner">
            {data.in_balance ? <CheckCircle2 size={32} className="text-positive" /> : <AlertTriangle size={32} className="text-brick" />}
            <div>
              <div className={`font-serif text-2xl ${data.in_balance ? "text-positive" : "text-brick"}`}>{data.in_balance ? "Seimbang / In Balance" : "Variance Detected"}</div>
              <div className="text-sm text-slate mt-1">Total variance: <span className="font-mono text-ink">RM <RM value={data.total_variance} /></span></div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-hairline p-4 rounded-sm"><div className="text-xs uppercase tracking-wider text-slate">Cached Total</div><div className="font-serif text-2xl text-ink mt-1">RM <RM value={data.total_cached} /></div></div>
            <div className="bg-white border border-hairline p-4 rounded-sm"><div className="text-xs uppercase tracking-wider text-slate">Summed Txns</div><div className="font-serif text-2xl text-ink mt-1">RM <RM value={data.total_transactions} /></div></div>
            <div className="bg-white border border-hairline p-4 rounded-sm"><div className="text-xs uppercase tracking-wider text-slate">Reconciled</div><div className="font-serif text-2xl text-ink mt-1">{data.members_reconciled}</div></div>
            <div className="bg-white border border-hairline p-4 rounded-sm"><div className="text-xs uppercase tracking-wider text-slate">Excluded (unconfirmed)</div><div className="font-serif text-2xl text-brass mt-1">{data.excluded_unconfirmed}</div></div>
          </div>

          <div className="bg-white border border-hairline rounded-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-hairline"><h3 className="font-serif text-lg text-ink">Variance Ledger</h3></div>
            {data.variances.length === 0 ? (
              <div className="px-6 py-10 text-center text-slate text-sm">Tiada variance — semua akaun seimbang. / No variances — all accounts balanced.</div>
            ) : (
              <table className="w-full">
                <thead><tr className="bg-paper border-y border-ink/20">
                  <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Membership</th>
                  <th className="text-left text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Name</th>
                  <th className="text-right text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Cached</th>
                  <th className="text-right text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Summed</th>
                  <th className="text-right text-xs uppercase tracking-wider text-slate font-medium py-3 px-4">Variance</th>
                </tr></thead>
                <tbody>
                  {data.variances.map((v) => (
                    <tr key={v.membership_no} className="ledger-row">
                      <td className="py-2.5 px-4"><StampSerial value={v.membership_no} /></td>
                      <td className="py-2.5 px-4 text-sm text-ink">{v.name}</td>
                      <td className="py-2.5 px-4 text-right"><RM value={v.cached_balance} /></td>
                      <td className="py-2.5 px-4 text-right"><RM value={v.summed_transactions} /></td>
                      <td className="py-2.5 px-4 text-right text-brick font-mono">{v.variance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}
